import React, {useEffect, useRef, useState} from "react";
import Autocomplete from "react-google-autocomplete";
import classnames from "classnames";
import {buildLocationString, parseGoogleAddressData} from "../utils/utils";
import Button from "react-bootstrap/Button";
import CircularProgress from "@mui/material/CircularProgress";
import AddIcon from '@mui/icons-material/Add';

export const CreateLocationInput = ( {
    onSave,
    isSaving,
    allowManualAdd = false,
    actionLabel = 'Add Location',
    // The + reads as "this appends a row". Set as Default assigns rather than
    // appends — and appends nothing at all when the ZIP is already saved.
    showAddIcon = true,
    // Default Settings only: its Default section can act on a saved row that is
    // already selected, so the button stays live without a resolved address.
    allowActionWithoutAddress = false,
    // Edit Location picker only: the field shows this location, like the
    // Calculate card. Undefined (Settings) keeps the field free-standing.
    location,
} ) => {
    const isShowingLocation = location !== undefined
    const [addressState, setAddressState] = useState(null)
    const [inputValue, setInputValue] = useState(() => buildLocationString(location))
    // A pick here or anywhere else (e.g. Customer moving Dealer) replaces the text.
    useEffect(() => {
        if (isShowingLocation) setInputValue(buildLocationString(location))
    }, [location, isShowingLocation])
    // Mirrors addressState so onChange reads the just-verified address even when
    // Google's write-back fires a change event right after onPlaceSelected.
    const pendingAddressRef = useRef(null)

    const setPendingAddress = (address) => {
        pendingAddressRef.current = address
        setAddressState(address)
    }

    const saveAddress = () => {
        onSave(addressState)
        setPendingAddress(null)
    }

    // Typing over a resolved address invalidates it — the text no longer
    // describes what Google verified, so the old result must not stay
    // submittable. Google writes the formatted string back through here on
    // selection, so a value that still matches the pending address is kept.
    const onInputChange = (e) => {
        const nextValue = e.target.value
        setInputValue(nextValue)
        const pending = pendingAddressRef.current
        if (pending && nextValue !== buildLocationString(pending)) {
            setPendingAddress(null)
        }
    }

    const isActionDisabled = !addressState && !allowActionWithoutAddress

    return (
        <div className={'CreateLocationInput'}>
            <Autocomplete
                placeholder={`Enter zipcode`}
                className={classnames('CreateLocationInput_input', {
                    disabled: !addressState
                })}
                style={{zIndex: "inherit"}}
                inputAutocompleteValue={addressState}
                apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
                value={inputValue}
                onChange={onInputChange}
                // Select the shown location so a typed ZIP replaces it; text left
                // without picking a suggestion is never applied.
                onFocus={isShowingLocation ? (e) => e.target.select() : undefined}
                onBlur={isShowingLocation ? () => setInputValue(buildLocationString(location)) : undefined}
                onPlaceSelected={(place) => {
                    const parsed = parseGoogleAddressData(place.address_components)
                    setInputValue(buildLocationString(parsed))
                    setPendingAddress(parsed)
                    // Without the Add step, Google's verification IS the save.
                    if (!allowManualAdd) onSave(parsed)
                }}
                options={{
                    types: ['postal_code'], // Restrict suggestions to postal codes only
                    componentRestrictions: { country: 'us' }, // Restrict to a specific country (e.g., US)
                    fields: ['address_components', 'name'] // Request only necessary data fields to control billing
                }}
            />
            {
                allowManualAdd &&
                <div>
                    <Button
                        className={classnames('CreateLocationInput_button', {
                            disabled: isActionDisabled
                        })}
                        onClick={saveAddress}
                        variant="contained"
                        disabled={isActionDisabled}
                    >
                        {isSaving && <CircularProgress size={16} color="inherit"/>}
                        {!isSaving && showAddIcon && <AddIcon fontSize="small"/>}
                        {actionLabel}
                    </Button>
                </div>
            }
            {
                // No button to hang the spinner off once Add is gone, so the
                // in-flight save reads next to the field instead.
                !allowManualAdd && isSaving &&
                <CircularProgress className={'CreateLocationInput_spinner'} size={16} color="inherit"/>
            }
        </div>
    )
}
